import { parseSearchParams } from "tap/utils/urlParams";

/**
 * The only parts of AG Grid's GridState we put in the URL.
 *
 * This is an allow-list rather than a deny-list because most of GridState
 * self-prunes but four keys never do. AG Grid's getters return `undefined` for
 * any untouched feature — `getFilterState` returns undefined for an empty filter
 * model, and `getColumnState` returns `sort: sortColumns.length ? {...} : undefined`,
 * with the same pattern for rowGroup, aggregation, pivot, columnPinning,
 * columnVisibility, columnGroup, rowGroupExpansion, pagination, scroll,
 * focusedCell and cellSelection. So those are already "absent until the user
 * does something".
 *
 * `version`, `columnOrder`, `columnSizing` and `sideBar` are the exceptions:
 * they're emitted unconditionally (and `autoSizing` injects `flex: 1` into most
 * of our colDefs, so `columnSizing` is never empty). Together they were ~840
 * characters of every URL even on a grid nobody had touched.
 *
 * Tradeoff: column width, order and visibility are no longer restored on
 * navigate-back. They're layout, not the query the user built up, and they're
 * what made the URL unreadable.
 */
export const PERSISTED_GRID_STATE_KEYS = Object.freeze(["sort", "filter"]);

/** Marks a descending column in the sort param, JSON:API style. */
const DESC_PREFIX = "-";

/**
 * Encodes a GridState sort into readable param values.
 *
 * Returns an array so the house `buildSearchString` joins it, which keeps
 * multi-column sort in one param. Direction rides on a `-` prefix rather than a
 * `colId:desc` pair because `query-string` escapes `:` to `%3A` (and `,` to
 * `%2C`) — of the plausible separators only `. - _ ~` survive unescaped, so a
 * colon format would have defeated the point of dropping base64.
 *
 * @param {Object} [sortState] - GridState.sort
 * @returns {Array<string>|undefined} e.g. ["-startDate", "name"], or undefined
 *   when nothing is sorted
 */
export function encodeSortParam(sortState) {
  const sortModel = sortState?.sortModel;
  if (!Array.isArray(sortModel)) return undefined;

  // AG Grid builds sortModel by assigning to sortColumns[sortIndex], so the
  // array can be sparse; filter before mapping.
  const encoded = sortModel
    .filter((item) => item?.colId)
    .map(({ colId, sort }) =>
      sort === "desc" ? `${DESC_PREFIX}${colId}` : colId,
    );

  return encoded.length ? encoded : undefined;
}

/**
 * Decodes the sort param back into a GridState sort.
 *
 * Accepts both a comma-joined string (what we write) and an array (what
 * query-string yields for a repeated param), so a hand-built URL works either
 * way.
 *
 * @param {*} param - Raw value from parseSearchParams
 * @returns {Object|undefined} GridState.sort, or undefined if absent/unusable
 */
export function decodeSortParam(param) {
  const tokens = Array.isArray(param)
    ? param
    : typeof param === "string"
      ? param.split(",")
      : null;
  if (!tokens) return undefined;

  const sortModel = tokens
    .map((token) => (typeof token === "string" ? token.trim() : ""))
    .filter(Boolean)
    .map((token) =>
      token.startsWith(DESC_PREFIX)
        ? { colId: token.slice(DESC_PREFIX.length), sort: "desc" }
        : { colId: token, sort: "asc" },
    )
    .filter(({ colId }) => colId);

  return sortModel.length ? { sortModel } : undefined;
}

/**
 * Encodes a GridState filter as JSON.
 *
 * The filter model stays JSON rather than a hand-rolled format: this grid uses
 * agSetColumnFilter (arrays of promo and client names that can contain commas),
 * agDateColumnFilter and agNumberColumnFilter, plus AG Grid's two-condition
 * AND/OR form. A custom codec there has to round-trip exactly or filters come
 * back subtly wrong, and the escaping alone would cost more than it saves.
 * Percent-encoded JSON is still readable in devtools and hand-editable.
 *
 * Only `filterModel` is persisted, not `advancedFilterModel` — this grid doesn't
 * enable advanced filter.
 *
 * @param {Object} [filterState] - GridState.filter
 * @returns {string|undefined} JSON, or undefined when no filter is active
 */
export function encodeFilterParam(filterState) {
  const filterModel = filterState?.filterModel;
  if (!filterModel || Object.keys(filterModel).length === 0) return undefined;

  try {
    return JSON.stringify(filterModel);
  } catch {
    return undefined;
  }
}

/**
 * Decodes the filter param back into a GridState filter.
 *
 * Returns undefined for anything unusable so a truncated or hand-mangled param
 * can't break the page.
 *
 * @param {*} param - Raw value from parseSearchParams
 * @returns {Object|undefined} GridState.filter, or undefined if absent/unusable
 */
export function decodeFilterParam(param) {
  if (typeof param !== "string" || param === "") return undefined;

  try {
    const filterModel = JSON.parse(param);
    if (!filterModel || typeof filterModel !== "object") return undefined;
    if (Array.isArray(filterModel)) return undefined;
    if (Object.keys(filterModel).length === 0) return undefined;
    return { filterModel };
  } catch {
    return undefined;
  }
}

/**
 * Splits a GridState into the URL params that represent it.
 *
 * Keys the user hasn't touched come back undefined, which `buildSearchString`
 * drops — so a pristine grid contributes no params at all.
 *
 * @param {Object} [state] - GridState from api.getState()
 * @param {{sort: string, filter: string}} paramNames - Param names to emit under
 * @returns {Object} Params to merge into the query string
 */
export function gridStateToParams(state, paramNames) {
  return {
    [paramNames.sort]: encodeSortParam(state?.sort),
    [paramNames.filter]: encodeFilterParam(state?.filter),
  };
}

/**
 * Rebuilds an AG Grid `initialState` from a query string.
 *
 * `partialColumnState: true` is required, not optional. Without it,
 * StateService.setColumnState builds a defaultState of
 * `{sort: null, ..., flex: null}` and applies it to every column *not* named in
 * the state we pass — so restoring a sort would strip `flex` off every other
 * column and wreck the autoSizing layout. With it set, defaultState starts empty
 * and only gains the sort keys, which is exactly what we want.
 *
 * @param {string} search - location.search
 * @param {{sort: string, filter: string}} paramNames - Param names to read
 * @returns {Object|undefined} GridState for `initialState`, or undefined when
 *   the URL carries no grid state
 */
export function paramsToGridState(search, paramNames) {
  const params = parseSearchParams(search);
  const sort = decodeSortParam(params[paramNames.sort]);
  const filter = decodeFilterParam(params[paramNames.filter]);

  if (!sort && !filter) return undefined;

  const state = { partialColumnState: true };
  if (sort) state.sort = sort;
  if (filter) state.filter = filter;
  return state;
}
